import * as THREE from 'three';
import Proton from 'three.proton.js';
import dot from './dot';
import PRNG from './PRNG';
import CrucibleScene from './crucibleScene';
import { BackgroundConfig } from './config';
import config from './config.json';
import CrucibleLoader from './crucibleLoader';
import CrucibleColors from './crucibleColors';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';

export default class Background {
  public group: THREE.Object3D;

  protected _crucibleScene: CrucibleScene;
  protected _config: BackgroundConfig;
  protected _textures: string[];
  protected _models: string[];
  protected _proton: any;
  protected _isLoaded: boolean;

  constructor(crucibleScene: CrucibleScene) {
    this.group = new THREE.Group();
    this._crucibleScene = crucibleScene;
    this._config = config.backgroundConfig;
    this._textures = this._config.nebulaTextures;
    this._models = this._config.nebulaModels;
    this._isLoaded = false;
    this._proton = null;
  }

  async load(loader: CrucibleLoader, prng: PRNG, colors: CrucibleColors) {
    this._isLoaded = false;
    this.group = new THREE.Group();
    this._proton = new Proton();

    // this._proton.addEmitter(this._createStarsEmitter(loader));
    this._proton.addRender(new Proton.SpriteRender(this._crucibleScene.scene));

    // Load the background as two layers of Nebula, loading a GLTF file for each
    // nebulaFirstLayer
    this._createNebulaMesh(loader, colors, prng, this._models[0]);

    // nebulaSecondLayer
    this._createNebulaMesh(loader, colors, prng, this._models[1]);

    this._isLoaded = true;
  }

  update(
    loader: CrucibleLoader,
    colors: CrucibleColors,
    prng: PRNG,
    mintNumber: number,
    christmasFlag: boolean
  ) {
    const maxValue = this._config.mintNumberMaxValue;
    mintNumber++; // so we don't start with 0
    if (mintNumber > maxValue) mintNumber = maxValue;

    const opacity = 1 - mintNumber / maxValue;
    this._createNebulaMesh(loader, colors, prng, this._models[2], opacity);

    if (mintNumber <= this._config.mintNumberLimit) {
      this._createMintAgeSpecialFX(loader, colors);
    }

    // if user has the christmas nft in their wallet
    if (christmasFlag) {
      this._createSnowEffect(loader, prng);
    }
  }

  animate() {
    if (this._isLoaded) this._proton.update();
  }

  async _createNebulaMesh(
    loader: CrucibleLoader,
    colors: CrucibleColors,
    prng: PRNG,
    nebulaModelUrl: string,
    opacity: number = 0
  ) {
    const textureUrl =
      this._textures[prng.randomInt('particle', this._textures.length - 1)];
    const color = colors.getTHREEColor();
    const material = this._createNebulaMaterial(loader, textureUrl, color);

    const nebulaMesh: GLTF = await loader.loadModel(nebulaModelUrl);
    nebulaMesh.scene.traverse((node) => {
      if (node instanceof THREE.Mesh) {
        node.material = material;
        if (opacity !== 0) node.material.opacity = opacity;
      }
    });

    this.group.add(nebulaMesh.scene);
  }

  _createNebulaMaterial(
    loader: CrucibleLoader,
    textureUrl: string,
    color: THREE.Color
  ) {
    const map = loader.textureLoader.load(textureUrl);
    const material = new THREE.MeshStandardMaterial({
      map: map,
      color: color,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: true
    });

    material.color.convertSRGBToLinear();

    return material;
  }

  _createSprite(loader: CrucibleLoader, spriteUrl: string, color: THREE.Color) {
    const map = loader.textureLoader.load(spriteUrl);
    const material = new THREE.SpriteMaterial({
      map: map,
      color: color,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: true
    });

    material.color.convertSRGBToLinear();

    return new THREE.Sprite(material);
  }

  _createStarsEmitter(loader: CrucibleLoader) {
    let emitter = new Proton.Emitter();
    emitter.rate = new Proton.Rate(
      new Proton.Span(80, 150),
      new Proton.Span(0.1, 0.3)
    );

    emitter.addInitialize(new Proton.Mass(1));
    emitter.addInitialize(new Proton.Radius(new Proton.Span(1.2, 1.8)));
    emitter.addInitialize(new Proton.Life(3, 10));
    emitter.addInitialize(
      new Proton.Body(
        this._createSprite(loader, dot, new THREE.Color(0xffffff))
      )
    );
    emitter.addInitialize(new Proton.Position(new Proton.BoxZone(3500)));

    emitter.addBehaviour(new Proton.Alpha(0, 1, Infinity, Proton.easeInQuart));

    emitter.p.x = 0;
    emitter.p.y = 0;
    emitter.emit();

    return emitter;
  }

  _createMintAgeSpecialFX(loader: CrucibleLoader, colors: CrucibleColors) {
    const color1 = colors.getTHREEColor();
    const color2 = colors.getTHREEColor();

    let specialEmitter = new Proton.Emitter();
    specialEmitter.rate = new Proton.Rate(
      new Proton.Span(5, 10),
      new Proton.Span(0.8, 1.5)
    );

    specialEmitter.addInitialize(new Proton.Mass(1));
    specialEmitter.addInitialize(new Proton.Radius(new Proton.Span(650, 700)));
    specialEmitter.addInitialize(new Proton.Life(2, 5));
    specialEmitter.addInitialize(
      new Proton.Body(this._createSprite(loader, dot, color1))
    );

    specialEmitter.addInitialize(new Proton.Position(new Proton.BoxZone(5000)));
    specialEmitter.addInitialize(
      new Proton.Velocity(250, new Proton.Vector3D(1, 1, 0), 180)
    );

    specialEmitter.addBehaviour(new Proton.Rotate('random', 'random'));
    specialEmitter.addBehaviour(
      new Proton.Alpha(0.2, 0, Infinity, Proton.easeOutQuart)
    );
    specialEmitter.addBehaviour(
      new Proton.Color(color1, color2, Infinity, Proton.easeOutQuart)
    );

    specialEmitter.p.x = 0;
    specialEmitter.p.y = 0;
    specialEmitter.emit();

    this._proton.addEmitter(specialEmitter);
  }

  _createSnowEffect(loader: CrucibleLoader, prng: PRNG) {
    // Create particle emitter
    const snowEmitter = new Proton.Emitter();
    snowEmitter.rate = new Proton.Rate(
      new Proton.Span(6, 20),
      new Proton.Span(0.2, 0.8)
    );

    const snowFlakeTextureUrl =
      this._config.snowFlakeTextures[
        prng.randomInt('particle', this._config.snowFlakeTextures.length - 1)
      ];

    const snowFlakeSprite = this._createSprite(
      loader,
      snowFlakeTextureUrl,
      new THREE.Color(0xffffff)
    );

    snowEmitter.addInitialize(new Proton.Mass(35));
    snowEmitter.addInitialize(new Proton.Radius(0.5, 1.5));
    snowEmitter.addInitialize(new Proton.Life(1, 10));
    snowEmitter.addInitialize(new Proton.Body(snowFlakeSprite));
    snowEmitter.addInitialize(
      new Proton.Position(new Proton.BoxZone(36, 10, 36))
    );

    snowEmitter.addBehaviour(new Proton.Rotate('random', 'random'));
    snowEmitter.addBehaviour(new Proton.RandomDrift(10, 1, 6, 0.1));
    snowEmitter.addBehaviour(
      new Proton.Color(0xf5fffe, 0x8afff1, Infinity, Proton.easeOutQuart)
    );
    snowEmitter.addBehaviour(new Proton.Gravity(1));
    snowEmitter.addBehaviour(new Proton.Scale(1, 0.9));
    snowEmitter.addBehaviour(
      new Proton.Alpha(0.7, 0, Infinity, Proton.easeInQuart)
    );

    snowEmitter.p.x = 0;
    snowEmitter.p.y = 40;
    snowEmitter.p.z = 0;
    snowEmitter.emit();

    this._proton.addEmitter(snowEmitter);
  }
}
